import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Upload,
  Trash2,
  LogOut,
  Image as ImageIcon,
  Loader2,
  CheckCircle2,
  ArrowLeft,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

function formatErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Admin() {
  const { user, loading, logout, authHeader, API } = useAuth();
  const navigate = useNavigate();
  const [photos, setPhotos] = useState([]);
  const [fetching, setFetching] = useState(true);

  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!loading && !user) navigate("/admin/login");
  }, [loading, user, navigate]);

  const loadPhotos = useCallback(async () => {
    setFetching(true);
    try {
      const { data } = await axios.get(`${API}/photos`);
      setPhotos(data);
    } finally {
      setFetching(false);
    }
  }, [API]);

  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    setPreview(f ? URL.createObjectURL(f) : null);
  };

  const onUpload = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!file) {
      setError("Please choose an image file.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("title", title || "Untitled");
      fd.append("caption", caption);
      fd.append("file", file);
      await axios.post(`${API}/photos`, fd, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
      });
      setSuccess("Photo uploaded.");
      setTitle("");
      setCaption("");
      setFile(null);
      setPreview(null);
      await loadPhotos();
      setTimeout(() => setSuccess(""), 4000);
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await axios.delete(`${API}/photos/${id}`, { headers: authHeader() });
      setPhotos((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#EAF1E8] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#2A4D35]" size={28} />
      </div>
    );
  }

  return (
    <div
      data-testid="admin-page"
      className="min-h-screen bg-[#EAF1E8] text-[#183321]"
    >
      <header className="border-b border-[#183321]/10 bg-[#EAF1E8]/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link
            to="/"
            data-testid="admin-home-link"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#4A6B53] hover:text-[#2A4D35]"
          >
            <ArrowLeft size={14} /> Back to site
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden md:block text-sm text-[#4A6B53]">
              Signed in as{" "}
              <span className="text-[#183321] font-semibold">
                {user?.username}
              </span>
            </span>
            <button
              data-testid="admin-logout"
              onClick={() => {
                logout();
                navigate("/admin/login");
              }}
              className="inline-flex items-center gap-2 px-4 py-2 border border-[#183321]/30 text-[#183321] text-xs uppercase tracking-widest font-semibold rounded-sm hover:bg-[#183321] hover:text-[#FDFDF9] transition-all"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-12 md:py-16">
        <div className="flex items-center gap-3 mb-4">
          <span className="w-10 h-px bg-[#183321]/40" />
          <span className="text-xs uppercase tracking-[0.25em] text-[#4A6B53] font-semibold">
            Admin · Project gallery
          </span>
        </div>
        <h1 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight">
          Upload field photos.
        </h1>
        <p className="mt-3 text-[#4A6B53] max-w-xl">
          Photos uploaded here appear publicly in the Projects section of the
          home page.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-12">
          {/* Upload form */}
          <section
            data-testid="upload-section"
            className="lg:col-span-5 bg-[#FDFDF9] border border-[#183321]/10 rounded-sm p-8"
          >
            <h2 className="font-serif text-2xl text-[#183321] mb-6">
              New photo
            </h2>
            <form onSubmit={onUpload} className="space-y-6">
              <div>
                <label className="text-xs uppercase tracking-widest text-[#4A6B53] font-semibold mb-2 block">
                  Title
                </label>
                <input
                  data-testid="upload-input-title"
                  type="text"
                  required
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full bg-transparent border-b border-[#183321]/30 py-3 text-[#183321] focus:outline-none focus:border-[#2A4D35]"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#4A6B53] font-semibold mb-2 block">
                  Caption (optional)
                </label>
                <textarea
                  data-testid="upload-input-caption"
                  rows={3}
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  className="w-full bg-transparent border-b border-[#183321]/30 py-3 text-[#183321] focus:outline-none focus:border-[#2A4D35] resize-none"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#4A6B53] font-semibold mb-3 block">
                  Image file
                </label>
                <label
                  data-testid="upload-dropzone"
                  className="block border border-dashed border-[#183321]/30 rounded-sm p-6 text-center cursor-pointer hover:border-[#2A4D35] hover:bg-[#EAF1E8] transition-colors"
                >
                  {preview ? (
                    <img
                      src={preview}
                      alt="preview"
                      className="max-h-64 mx-auto rounded-sm object-contain"
                    />
                  ) : (
                    <div className="py-6">
                      <ImageIcon
                        size={28}
                        className="mx-auto text-[#4A6B53] mb-3"
                      />
                      <div className="text-sm text-[#4A6B53]">
                        Click to choose an image
                      </div>
                      <div className="text-xs text-[#4A6B53]/70 mt-1">
                        JPG, PNG, WEBP · max 10MB
                      </div>
                    </div>
                  )}
                  <input
                    data-testid="upload-input-file"
                    type="file"
                    accept="image/*"
                    onChange={onFileChange}
                    className="hidden"
                  />
                </label>
                {file && (
                  <div className="text-xs text-[#4A6B53] mt-2">
                    {file.name} · {(file.size / 1024).toFixed(0)} KB
                  </div>
                )}
              </div>

              {error && (
                <div
                  data-testid="upload-error"
                  className="text-sm text-[#C26D5C] bg-[#C26D5C]/10 border border-[#C26D5C]/30 rounded-sm px-4 py-3"
                >
                  {error}
                </div>
              )}
              {success && (
                <div
                  data-testid="upload-success"
                  className="flex items-center gap-2 text-sm text-[#2A4D35]"
                >
                  <CheckCircle2 size={16} />
                  {success}
                </div>
              )}

              <button
                data-testid="upload-submit"
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 px-7 py-4 bg-[#2A4D35] text-[#FDFDF9] text-sm uppercase tracking-widest font-semibold rounded-sm hover:bg-[#3C664A] disabled:opacity-60 transition-all"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Uploading…
                  </>
                ) : (
                  <>
                    <Upload size={16} /> Upload photo
                  </>
                )}
              </button>
            </form>
          </section>

          {/* Gallery */}
          <section
            data-testid="gallery-section"
            className="lg:col-span-7"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl text-[#183321]">
                Library{" "}
                <span className="text-[#4A6B53] text-base">
                  ({photos.length})
                </span>
              </h2>
            </div>

            {fetching ? (
              <div className="text-[#4A6B53] text-sm flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Loading photos…
              </div>
            ) : photos.length === 0 ? (
              <div className="text-[#4A6B53] text-sm">
                No photos yet. Upload your first one.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {photos.map((p) => (
                  <article
                    key={p.id}
                    data-testid={`admin-photo-${p.id}`}
                    className="bg-[#FDFDF9] border border-[#183321]/10 rounded-sm overflow-hidden group"
                  >
                    <div className="aspect-[4/3] overflow-hidden bg-[#EAF1E8]">
                      <img
                        src={p.image_url}
                        alt={p.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                    <div className="p-4">
                      <div className="font-serif text-lg text-[#183321] leading-tight">
                        {p.title}
                      </div>
                      {p.caption && (
                        <div className="text-xs text-[#4A6B53] mt-1 line-clamp-2">
                          {p.caption}
                        </div>
                      )}
                      <div className="flex items-center justify-between mt-3">
                        <span className="text-[10px] uppercase tracking-widest text-[#4A6B53]/70">
                          {new Date(p.created_at).toLocaleDateString()}
                        </span>
                        <button
                          data-testid={`admin-delete-${p.id}`}
                          onClick={() => onDelete(p.id)}
                          className="inline-flex items-center gap-1 text-xs text-[#C26D5C] hover:text-[#a55344] transition-colors"
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
